import { TrashIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import cn from 'classnames';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useUserStore } from '../../../components/userStore';

const AuthorizedServiceForm = ({
	onFormChange,
	serviceData: {
		procedure_code = '',
		procedure_modifier = '',
		service_unit = '',
		service_unit_cost = 0,
		duration = 0,
		duration_unit = 'days',
		procedure_code_description = '',
		taxonomy_sku_id = '',
		taxonomy_sku = {},
	},
	exServiceId,
	hideLabels = true,
	onRemove,
}) => {
	const { user } = useUserStore();
	const [skuId, setSKUId] = useState(taxonomy_sku_id);
	const [procCode, setProcCode] = useState(procedure_code);
	const [procModifier, setProcModifier] = useState(procedure_modifier);
	const [serviceUnit, setServiceUnit] = useState(service_unit);
	const [suCost, setSUCost] = useState(service_unit_cost);
	const [dur, setDuration] = useState(duration);
	const [durationUnit, setDurationUnit] = useState(duration_unit);
	const [codeDesc, setCodeDesc] = useState(procedure_code_description);
	const skuQuery = useQuery({
		queryKey: ['service-skus', user.coord_agency.id, exServiceId],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/service-skus/${exServiceId}`,
			);
			return data;
		},
		enabled: !isEmpty(exServiceId),
	});
	const handleFormChange = () => {
		onFormChange({
			procedure_code: procCode,
			procedure_modifier: procModifier,
			service_unit: serviceUnit,
			service_unit_cost: suCost,
			duration: durationUnit === 'once' ? 0 : dur,
			duration_unit: durationUnit,
			procedure_code_description: codeDesc,
			taxonomy_sku_id: skuId,
		});
	};

	useEffect(() => {
		setProcCode(procedure_code);
		setProcModifier(procedure_modifier);
		setServiceUnit(service_unit);
		setSUCost(service_unit_cost);
		setDuration(duration);
		setDurationUnit(duration_unit);
		setCodeDesc(procedure_code_description);
		setSKUId(taxonomy_sku_id);
	}, [
		procedure_code,
		procedure_modifier,
		service_unit,
		service_unit_cost,
		duration,
		duration_unit,
		procedure_code_description,
		taxonomy_sku_id,
	]);

	if (skuQuery.isFetched && skuQuery.data.length === 0)
		return <div className="mt-2">No SKUs available</div>;
	if (skuQuery.isFetching) return <div className="mt-2">Loading SKUs...</div>;

	return (
		<>
			<div className="col-span-2 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					SKU
				</label>
				<select
					className="select select-bordered select-xs"
					value={skuId}
					onChange={({ target }) => setSKUId(target.value)}
					required
				>
					<option value="-1" disabled>
						Select SKU
					</option>
					{taxonomy_sku?.id &&
						!skuQuery.data?.find(({ id }) => id === skuId) && (
							<option key={taxonomy_sku?.id} value={taxonomy_sku?.id}>
								{taxonomy_sku?.label}
							</option>
						)}
					{skuQuery.data?.map(({ id, label }) => (
						<option key={id} value={id}>
							{label}
						</option>
					))}
				</select>
			</div>
			<div className="col-span-1 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					Procedure Code
				</label>
				<input
					type="text"
					className="input input-bordered input-xs"
					value={procCode}
					onChange={({ target }) => setProcCode(target.value)}
					onBlur={handleFormChange}
				/>
			</div>
			<div className="col-span-2 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					Procedure Modifier
				</label>
				<input
					type="text"
					className="input input-bordered input-xs"
					value={procModifier}
					onChange={({ target }) => setProcModifier(target.value)}
					onBlur={handleFormChange}
				/>
			</div>
			<div className="col-span-1 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					Service Unit
				</label>
				<input
					type="text"
					className="input input-bordered input-xs"
					value={serviceUnit}
					onChange={({ target }) => setServiceUnit(target.value)}
					onBlur={handleFormChange}
				/>
			</div>
			<div className="col-span-2 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					Max Service Unit Cost
				</label>
				<input
					type="number"
					className="w-full pr-0 text-right input input-bordered input-xs"
					value={suCost}
					step={0.01}
					min={0}
					onChange={({ target }) => setSUCost(target.value)}
					onBlur={handleFormChange}
				/>
			</div>
			<div className="col-span-2 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					Duration
				</label>
				<div className="flex flex-row gap-1">
					<input
						type="number"
						size={6}
						step={1}
						min={0}
						className="w-12 pr-0 text-right input input-bordered input-xs"
						value={dur}
						onChange={({ target }) => setDuration(target.value)}
						placeholder="0"
						onBlur={handleFormChange}
						disabled={durationUnit === 'once'}
					/>
					<select
						className="w-full select select-bordered select-xs"
						value={durationUnit}
						onChange={({ target }) => setDurationUnit(target.value)}
						onBlur={handleFormChange}
					>
						<option value="once">Once</option>
						<option value="days">Days</option>
						<option value="weeks">Weeks</option>
						<option value="months">Months</option>
						<option value="years">Years</option>
					</select>
				</div>
			</div>
			<div className="col-span-2 form-control">
				<label
					className={cn('text-xs', 'label', 'label-text', {
						hidden: hideLabels,
					})}
				>
					Code Description
				</label>
				<div className="flex flex-row gap-2">
					<input
						type="text"
						className="input input-bordered input-xs"
						value={codeDesc}
						onChange={({ target }) => setCodeDesc(target.value)}
						onBlur={handleFormChange}
					/>
					<button
						className="text-white btn btn-xs btn-error"
						onClick={onRemove}
					>
						<TrashIcon className="w-4 h-4" />
					</button>
				</div>
			</div>
		</>
	);
};

AuthorizedServiceForm.propTypes = {
	onFormChange: PropTypes.func.isRequired,
	serviceData: PropTypes.object,
	exServiceId: PropTypes.number.isRequired,
	hideLabels: PropTypes.bool,
	onRemove: PropTypes.func.isRequired,
};

export default AuthorizedServiceForm;
